/**
 * tws-mailchimp.js
 * Client-side handler for the contact form and newsletter signup form.
 * Sends data to the Vercel API (window.TWS.apiUrl).
 *
 * Handles:
 *   .js-tws-contact-form    → POST /api/contact
 *   .js-tws-newsletter-form → POST /api/subscribe
 *   .js-tws-event-form      → POST /api/subscribe (name + email + phone, tagged by data-tws-source)
 */

(function () {
  'use strict';

  const apiUrl = (window.TWS && window.TWS.apiUrl) ? window.TWS.apiUrl.replace(/\/$/, '') : '';

  if (!apiUrl || apiUrl.includes('YOUR-VERCEL-APP')) {
    console.warn('[TWS] Vercel API URL is not configured. Set it in Theme → Customize → Integrations.');
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function setFormState(form, state) {
    const btn = form.querySelector('[type="submit"]');
    const successEl = form.querySelector('[data-tws-success]');
    const errorEl   = form.querySelector('[data-tws-error]');

    if (state === 'loading') {
      if (btn) { btn.disabled = true; btn.dataset.origText = btn.textContent; btn.textContent = 'Sending…'; }
      if (successEl) successEl.hidden = true;
      if (errorEl)   errorEl.hidden   = true;
    }

    if (state === 'reset') {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.origText || btn.textContent; }
    }

    if (state === 'success') {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.origText || btn.textContent; }
      if (successEl) successEl.hidden = false;
      form.reset();
      form.dispatchEvent(new CustomEvent('tws:success', { bubbles: true }));
    }

    if (state === 'error') {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.origText || btn.textContent; }
      if (errorEl) errorEl.hidden = false;
    }
  }

  function showMessage(el, message) {
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async function postJson(endpoint, body) {
    const res = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    return { ok: res.ok, data };
  }

  // ─── Contact Form ────────────────────────────────────────────────────────────

  function initContactForm() {
    const form = document.querySelector('.js-tws-contact-form');
    if (!form) return;

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const successEl = form.querySelector('[data-tws-success]');
      const errorEl   = form.querySelector('[data-tws-error]');

      setFormState(form, 'loading');

      const subscribeCheckbox = form.querySelector('[name="tws_subscribe"]');

      const payload = {
        name:      (form.querySelector('[name="contact_name"]')  || {}).value || '',
        email:     (form.querySelector('[name="contact_email"]') || {}).value || '',
        phone:     (form.querySelector('[name="contact_phone"]') || {}).value || '',
        message:   (form.querySelector('[name="contact_body"]')  || {}).value || '',
        subscribe: subscribeCheckbox ? subscribeCheckbox.checked : false,
      };

      try {
        const { ok, data } = await postJson('/api/contact', payload);

        if (ok) {
          showMessage(successEl, data.message || 'Thanks for reaching out. We will be in touch soon.');
          setFormState(form, 'success');
        } else {
          showMessage(errorEl, data.error || 'Something went wrong. Please try again.');
          setFormState(form, 'error');
        }
      } catch (err) {
        console.error('[TWS] Contact form error:', err);
        showMessage(errorEl, 'Network error. Please check your connection and try again.');
        setFormState(form, 'error');
      }
    });
  }

  // ─── Newsletter Form ─────────────────────────────────────────────────────────

  function initNewsletterForm() {
    const forms = document.querySelectorAll('.js-tws-newsletter-form');
    forms.forEach(function (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();

        const successEl = form.querySelector('[data-tws-success]');
        const errorEl   = form.querySelector('[data-tws-error]');
        const emailInput = form.querySelector('[name="newsletter_email"]');

        if (!emailInput) return;

        setFormState(form, 'loading');

        try {
          const { ok, data } = await postJson('/api/subscribe', {
            email: emailInput.value,
            source: form.dataset.twsSource || 'newsletter',
          });

          if (ok) {
            showMessage(successEl, data.message || 'Thank you! Check your inbox to confirm.');
            setFormState(form, 'success');
          } else {
            showMessage(errorEl, data.error || 'Something went wrong. Please try again.');
            setFormState(form, 'error');
          }
        } catch (err) {
          console.error('[TWS] Newsletter form error:', err);
          showMessage(errorEl, 'Network error. Please check your connection and try again.');
          setFormState(form, 'error');
        }
      });
    });
  }

  // ─── Event Signup Form (banner-triggered popup: name + email + phone) ────────

  function initEventForm() {
    const forms = document.querySelectorAll('.js-tws-event-form');
    forms.forEach(function (form) {
      form.addEventListener('submit', async function (e) {
        e.preventDefault();

        const successEl = form.querySelector('[data-tws-success]');
        const errorEl   = form.querySelector('[data-tws-error]');
        const nameInput  = form.querySelector('[name="event_name"]');
        const emailInput = form.querySelector('[name="event_email"]');
        const phoneInput = form.querySelector('[name="event_phone"]');

        if (!emailInput) return;

        setFormState(form, 'loading');

        const payload = {
          name:   nameInput ? nameInput.value : '',
          email:  emailInput.value,
          phone:  phoneInput ? phoneInput.value : '',
          source: form.dataset.twsSource || 'event-signup',
        };

        try {
          const { ok, data } = await postJson('/api/subscribe', payload);

          if (ok) {
            showMessage(successEl, data.message || 'Thank you! We can’t wait to see you.');
            setFormState(form, 'success');
          } else {
            showMessage(errorEl, data.error || 'Something went wrong. Please try again.');
            setFormState(form, 'error');
          }
        } catch (err) {
          console.error('[TWS] Event signup form error:', err);
          showMessage(errorEl, 'Network error. Please check your connection and try again.');
          setFormState(form, 'error');
        }
      });
    });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initContactForm();
      initNewsletterForm();
      initEventForm();
    });
  } else {
    initContactForm();
    initNewsletterForm();
    initEventForm();
  }
})();
